import type { Me } from '@gradex-hire/contracts';
import { useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '../../app/layouts/AuthLayout.js';
import { PageHeader } from '../../ui/components/PageHeader.js';
import '../../ui/components/button.css';
import '../../ui/components/field.css';
import './sign-in.css';

const session = () => import('../../lib/session/session.js');
// Someone hiring can ask for a school invitation here (H4.1); it loads only for them.
const HiringWelcome = lazy(() =>
  import('../organisations/HiringWelcome.js').then((module) => ({
    default: module.HiringWelcome,
  })),
);
// The test-session banner loads only for a test session, keeping the public bundle small.
const SandboxBanner = lazy(() =>
  import('../sandbox/SandboxBanner.js').then((module) => ({
    default: module.StandaloneSandboxBanner,
  })),
);

/**
 * "Your account is being set up" (`/welcome`): where a signed-in account
 * waits while it is `PENDING` (until onboarding or an admin activates it).
 * Signed-out visitors are offered sign-in; active accounts move on to their
 * area.
 */
export function WelcomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null | undefined>();
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let active = true;
    void session().then(async ({ meQuery, destinationFor }) => {
      const { getQueryClient } = await import('../../lib/api/query.js');
      try {
        const account = await getQueryClient().query(meQuery);
        if (!active) return;
        const destination = account ? destinationFor(account) : '/welcome';
        if (destination !== '/welcome') {
          void navigate({ to: destination, replace: true });
          return;
        }
        setMe(account);
      } catch {
        if (active) setMe(null);
      }
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  const signOut = async () => {
    setSigningOut(true);
    try {
      await (await session()).signOut();
    } finally {
      void navigate({ to: '/sign-in', viewTransition: true });
    }
  };

  return (
    <AuthLayout>
      <div className="sign-in">
        <PageHeader title={t('welcome.title')} description={t('welcome.description')} />
        {me === undefined ? (
          <p className="sign-in__status" role="status">
            {t('welcome.loading')}
          </p>
        ) : me === null ? (
          <div className="sign-in__form">
            <p>{t('welcome.signedOut')}</p>
            <a className="btn btn--primary sign-in__submit" href="/sign-in">
              {t('shell.account.signIn')}
            </a>
          </div>
        ) : (
          <div className="sign-in__form">
            {me.kind === 'SCHOOL' ? (
              <Suspense fallback={null}>
                <HiringWelcome />
              </Suspense>
            ) : (
              <p>{t('welcome.body')}</p>
            )}
            <p className="sign-in__fineprint">
              {t('welcome.signedInAs', { contact: me.email ?? me.phone ?? '' })}
            </p>
            {me.sandbox ? null : (
              <div className="sign-in__actions">
                <button
                  className="btn btn--secondary"
                  type="button"
                  disabled={signingOut}
                  onClick={() => void signOut()}
                >
                  {t('shell.account.signOut')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {me?.sandbox ? (
        <Suspense fallback={null}>
          <SandboxBanner />
        </Suspense>
      ) : null}
    </AuthLayout>
  );
}
